/**
 * Email Intake Dashboard
 *
 * Main inbox for PMs to review and process incoming project kickoff emails.
 * Shows extraction status, confidence, and quick actions.
 *
 * Workflow:
 * 1. Emails arrive via Power Automate webhook
 * 2. AI extracts project data automatically
 * 3. PMs review extracted data here
 * 4. Approve (creates work order) or request clarification
 */

import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const apiScopes = [
  "api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user",
];

// Status badge colors and labels
const STATUS_CONFIG = {
  pending: { bg: "bg-slate-100", text: "text-gray-600", label: "Pending" },
  extracting: { bg: "bg-blue-100", text: "text-blue-600", label: "Extracting..." },
  extracted: { bg: "bg-green-100", text: "text-green-600", label: "Ready for Review" },
  reviewed: { bg: "bg-purple-100", text: "text-purple-600", label: "Reviewed" },
  converted: { bg: "bg-emerald-100", text: "text-emerald-600", label: "Converted" },
  failed: { bg: "bg-red-100", text: "text-red-600", label: "Failed" },
  discarded: { bg: "bg-slate-100", text: "text-gray-500", label: "Discarded" },
};

// Confidence indicator component
function ConfidenceIndicator({ confidence }) {
  if (confidence === null || confidence === undefined) return null;

  const pct = Math.round(confidence * 100);
  const color =
    pct >= 90 ? "text-green-600 bg-green-50" :
    pct >= 70 ? "text-yellow-600 bg-yellow-50" :
    "text-red-600 bg-red-50";

  return (
    <span className={`text-xs px-2 py-0.5 rounded ${color}`}>
      {pct}% confident
    </span>
  );
}

// Status badge component
function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`text-xs px-2 py-1 rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

// Individual intake card
function IntakeCard({ intake, onSelect, onQuickAction, isSelected }) {
  const {
    id,
    email_subject,
    email_from,
    email_received_at,
    status,
    extraction_confidence,
    customer_name,
    project_name,
    ship_date,
    error_message,
  } = intake;

  // Format the received date
  const receivedDate = email_received_at
    ? new Date(email_received_at).toLocaleString()
    : "Unknown";

  // Calculate urgency based on ship date
  const daysUntilShip = ship_date
    ? Math.ceil((new Date(ship_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const isUrgent = daysUntilShip !== null && daysUntilShip <= 3;
  const isReadyForReview = status === "extracted";

  return (
    <div
      className={`
        border rounded-lg p-4 cursor-pointer transition-all
        ${isSelected ? "border-blue-500 bg-blue-50/50 ring-2 ring-blue-200" : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}
        ${isUrgent && isReadyForReview ? "border-l-4 border-l-orange-500" : ""}
      `}
      onClick={() => onSelect(intake)}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate" title={email_subject}>
            {email_subject || "No subject"}
          </h3>
          <p className="text-sm text-gray-500 truncate">
            From: {email_from || "Unknown"}
          </p>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Extracted data preview */}
      {(customer_name || project_name) && (
        <div className="bg-slate-200 rounded p-2 mb-2 text-sm">
          {customer_name && (
            <div className="flex gap-2">
              <span className="text-gray-500 w-20 shrink-0">Customer:</span>
              <span className="font-medium text-gray-800">{customer_name}</span>
            </div>
          )}
          {project_name && (
            <div className="flex gap-2">
              <span className="text-gray-500 w-20 shrink-0">Project:</span>
              <span className="text-gray-700">{project_name}</span>
            </div>
          )}
          {ship_date && (
            <div className="flex gap-2">
              <span className="text-gray-500 w-20 shrink-0">Ship Date:</span>
              <span className={isUrgent ? "text-orange-600 font-medium" : "text-gray-700"}>
                {ship_date}
                {daysUntilShip !== null && (
                  <span className="text-xs ml-1">
                    ({daysUntilShip <= 0 ? "OVERDUE" : `${daysUntilShip}d`})
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error_message && status === "failed" && (
        <div className="bg-red-50 text-red-700 text-sm rounded p-2 mb-2">
          {error_message}
        </div>
      )}

      {/* Footer row */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{receivedDate}</span>
          <ConfidenceIndicator confidence={extraction_confidence} />
        </div>

        {/* Quick actions */}
        {isReadyForReview && (
          <div className="flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onQuickAction(id, "approve");
              }}
              className="text-xs px-2 py-1 bg-green-600 text-gray-900 rounded hover:bg-green-700 transition"
            >
              Review
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onQuickAction(id, "discard");
              }}
              className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition"
            >
              Skip
            </button>
          </div>
        )}

        {status === "failed" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onQuickAction(id, "reprocess");
            }}
            className="text-xs px-2 py-1 bg-blue-600 text-gray-900 rounded hover:bg-blue-700 transition"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

// Detail panel for selected intake
function IntakeDetailPanel({ intake, onAction, loading, authHeader }) {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Load preview when intake changes
  useEffect(() => {
    if (!intake || intake.status !== "extracted") {
      setPreview(null);
      return;
    }

    async function loadPreview() {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const res = await fetch(`/api/email-intake/${intake.id}/preview`, {
          headers: authHeader,
        });
        if (!res.ok) throw new Error("Failed to load preview");
        const data = await res.json();
        setPreview(data);
      } catch (err) {
        setPreviewError(err.message);
      } finally {
        setPreviewLoading(false);
      }
    }

    loadPreview();
  }, [intake?.id, intake?.status, authHeader]);

  if (!intake) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Select an email to view details
      </div>
    );
  }

  const extractedData = intake.extracted_data || {};
  const skus = extractedData.skus || [];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b bg-white">
        <h2 className="font-semibold text-lg text-gray-900 mb-1">
          {intake.email_subject || "No subject"}
        </h2>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>From: {intake.email_from}</span>
          <StatusBadge status={intake.status} />
          <ConfidenceIndicator confidence={intake.extraction_confidence} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Extracted Data Section */}
        {intake.status === "extracted" && (
          <div className="space-y-4">
            {/* Project Info */}
            <div className="bg-slate-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Extracted Project Info</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Customer:</span>
                  <p className="font-medium">{extractedData.customer_name || "Not found"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Project:</span>
                  <p className="font-medium">{extractedData.project_name || "Not found"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Ship Date:</span>
                  <p className="font-medium">{extractedData.ship_date || "Not found"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Quote #:</span>
                  <p className="font-medium">{extractedData.quote_number || "Not found"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Contact:</span>
                  <p className="font-medium">{extractedData.contact_name || "Not found"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Rush:</span>
                  <p className={extractedData.is_rush ? "font-medium text-orange-600" : ""}>
                    {extractedData.is_rush ? "YES" : "No"}
                  </p>
                </div>
              </div>
            </div>

            {/* SKUs */}
            {skus.length > 0 && (
              <div className="bg-slate-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">
                  SKUs ({skus.length})
                </h3>
                <div className="space-y-2">
                  {skus.map((sku, i) => (
                    <div key={i} className="bg-white rounded p-3 border text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{sku.sku_name || `SKU ${i + 1}`}</p>
                          <p className="text-gray-500">Qty: {sku.quantity || 1}</p>
                        </div>
                        <div className="flex gap-1">
                          {sku.needs_model && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">3D</span>
                          )}
                          {sku.needs_paint && (
                            <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded">PAINT</span>
                          )}
                          {sku.needs_graphics && (
                            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-600 rounded">GFX</span>
                          )}
                          {sku.needs_opti && (
                            <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded">OPTI</span>
                          )}
                        </div>
                      </div>
                      {sku.notes && (
                        <p className="text-gray-500 text-xs mt-1">{sku.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Clarification Questions */}
            {preview?.clarification_questions?.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-medium text-yellow-800 mb-2">
                  Suggested Clarifications
                </h3>
                <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                  {preview.clarification_questions.map((q, i) => (
                    <li key={i}>{typeof q === 'object' ? q.question : q}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Special Requirements */}
            {extractedData.special_requirements && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-800 mb-2">Special Requirements</h3>
                <p className="text-sm text-blue-700">{extractedData.special_requirements}</p>
              </div>
            )}
          </div>
        )}

        {/* Original Email Body */}
        <div className="mt-4 bg-white border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Original Email</h3>
          <div className="text-sm text-gray-600 whitespace-pre-wrap max-h-64 overflow-y-auto">
            {intake.email_body_text || "No email body available"}
          </div>
        </div>

        {/* Attachments */}
        {intake.attachments && intake.attachments.length > 0 && (
          <div className="mt-4 bg-slate-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Attachments</h3>
            <div className="space-y-1">
              {intake.attachments.map((att, i) => (
                <div key={i} className="text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <span>{att.name}</span>
                  {att.size && <span className="text-gray-500">({Math.round(att.size / 1024)} KB)</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {intake.status === "extracted" && (
        <div className="p-4 border-t bg-slate-200 flex gap-2">
          <button
            onClick={() => onAction(intake.id, "approve")}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-green-600 text-gray-900 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
          >
            {loading ? "Processing..." : "Create Work Order"}
          </button>
          <button
            onClick={() => onAction(intake.id, "discard")}
            disabled={loading}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
          >
            Discard
          </button>
        </div>
      )}

      {intake.status === "failed" && (
        <div className="p-4 border-t bg-slate-200">
          <button
            onClick={() => onAction(intake.id, "reprocess")}
            disabled={loading}
            className="w-full px-4 py-2 bg-blue-600 text-gray-900 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? "Reprocessing..." : "Retry Extraction"}
          </button>
        </div>
      )}
    </div>
  );
}

// Stats bar component
function StatsBar({ stats }) {
  return (
    <div className="flex gap-4 text-sm">
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-green-500"></span>
        <span className="text-gray-600">Ready:</span>
        <span className="font-medium">{stats.extracted || 0}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
        <span className="text-gray-600">Processing:</span>
        <span className="font-medium">{(stats.pending || 0) + (stats.extracting || 0)}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-red-500"></span>
        <span className="text-gray-600">Failed:</span>
        <span className="font-medium">{stats.failed || 0}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
        <span className="text-gray-600">Converted:</span>
        <span className="font-medium">{stats.converted || 0}</span>
      </div>
    </div>
  );
}

// Main dashboard component
export default function EmailIntakeDashboard({ onNavigateToReview }) {
  const { instance, accounts } = useMsal();

  // State
  const [intakes, setIntakes] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [authHeader, setAuthHeader] = useState({});

  // Stats calculated from intakes
  const stats = intakes.reduce((acc, intake) => {
    acc[intake.status] = (acc[intake.status] || 0) + 1;
    return acc;
  }, {});

  // Acquire access token
  const acquireAccessToken = useCallback(async () => {
    if (accounts.length === 0) throw new Error("No logged in user.");
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.loginRedirect({ scopes: apiScopes, account: accounts[0] });
        return null;
      }
      throw error;
    }
  }, [instance, accounts]);

  // Load intakes
  const loadIntakes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const header = { Authorization: `Bearer ${accessToken}` };
      setAuthHeader(header);

      let url = "/api/email-intake?limit=100";
      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }

      const res = await fetch(url, { headers: header });
      if (!res.ok) throw new Error("Failed to load intakes");

      const data = await res.json();
      setIntakes(data.intakes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken, statusFilter]);

  // Initial load and polling
  useEffect(() => {
    loadIntakes();

    // Poll every 30 seconds for new emails
    const pollId = setInterval(loadIntakes, 30000);
    return () => clearInterval(pollId);
  }, [loadIntakes]);

  // Handle actions
  const handleAction = async (intakeId, action) => {
    setActionLoading(true);
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const header = { Authorization: `Bearer ${accessToken}` };

      let endpoint, method, body;

      switch (action) {
        case "approve":
          // Navigate to the full review UI instead of auto-approving
          if (onNavigateToReview) {
            onNavigateToReview(intakeId);
            return;
          }
          // Fallback: direct create
          endpoint = `/api/email-intake/${intakeId}/create-project`;
          method = "POST";
          body = {};
          break;

        case "discard":
          endpoint = `/api/email-intake/${intakeId}/discard`;
          method = "POST";
          body = { reason: "Discarded by PM" };
          break;

        case "reprocess":
          endpoint = `/api/email-intake/${intakeId}/reprocess`;
          method = "POST";
          body = {};
          break;

        default:
          throw new Error(`Unknown action: ${action}`);
      }

      const res = await fetch(endpoint, {
        method,
        headers: { ...header, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Action failed");
      }

      // Reload intakes
      await loadIntakes();

      // Clear selection if it was the actioned item
      if (selectedIntake?.id === intakeId) {
        setSelectedIntake(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Filter intakes for display
  const filteredIntakes = statusFilter === "all"
    ? intakes
    : intakes.filter((i) => i.status === statusFilter);

  // Sort: ready for review first, then by date
  const sortedIntakes = [...filteredIntakes].sort((a, b) => {
    // Extracted (ready for review) first
    if (a.status === "extracted" && b.status !== "extracted") return -1;
    if (b.status === "extracted" && a.status !== "extracted") return 1;
    // Then by date descending
    return new Date(b.email_received_at) - new Date(a.email_received_at);
  });

  return (
    <div className="h-full flex flex-col bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-semibold text-gray-900">Email Intake</h1>
          <button
            onClick={loadIntakes}
            disabled={loading}
            className="text-sm px-3 py-1.5 bg-slate-100 text-gray-700 rounded hover:bg-gray-200 transition disabled:opacity-50"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {/* Stats and filters */}
        <div className="flex items-center justify-between">
          <StatsBar stats={stats} />

          <div className="flex gap-2">
            {["all", "extracted", "pending", "failed", "converted", "discarded"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`text-xs px-3 py-1 rounded transition ${
                  statusFilter === status
                    ? "bg-blue-600 text-gray-900"
                    : "bg-slate-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {status === "all" ? "All" : STATUS_CONFIG[status]?.label || status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 text-red-700 text-sm">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Main content - split view */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Intake list */}
        <div className="w-1/2 border-r overflow-y-auto p-4 space-y-3">
          {loading && intakes.length === 0 ? (
            <div className="text-center text-gray-500 py-8">Loading emails...</div>
          ) : sortedIntakes.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {statusFilter === "all"
                ? "No emails in the inbox"
                : `No emails with status "${statusFilter}"`}
            </div>
          ) : (
            sortedIntakes.map((intake) => (
              <IntakeCard
                key={intake.id}
                intake={intake}
                onSelect={setSelectedIntake}
                onQuickAction={handleAction}
                isSelected={selectedIntake?.id === intake.id}
              />
            ))
          )}
        </div>

        {/* Right: Detail panel */}
        <div className="w-1/2 bg-white overflow-hidden">
          <IntakeDetailPanel
            intake={selectedIntake}
            onAction={handleAction}
            loading={actionLoading}
            authHeader={authHeader}
          />
        </div>
      </div>
    </div>
  );
}
